// MailboxCategories — the Outlook categories (colored tags) of the
// mailbox you're working in, shown under SEARCH FOLDERS in the email
// sidebar. Read-only: it is a legend of the tags, so Joey sees the same
// names and colors he uses in Outlook (issue #985, item 4). Follows the
// selected mailbox, so switching to a shared mailbox shows its tags.
// Hidden when there are none or Microsoft can't answer: categories are
// a convenience and must never get in the way of the folder list.

import { useEffect, useState } from "react";

import { api, type OutlookCategoryRow } from "../../api";
import { useSelectedMailbox } from "../../utils/email-selected-mailbox";

// Microsoft's category colors are presets, not hex. These match Outlook's
// own palette closely enough that a tag is recognizable at a glance.
const PRESET_COLORS: Record<string, string> = {
  preset0: "#e7a1a2",
  preset1: "#f9ba89",
  preset2: "#f7dd8f",
  preset3: "#fcfa90",
  preset4: "#78d168",
  preset5: "#9fdcc9",
  preset6: "#c6d2b0",
  preset7: "#9db7e8",
  preset8: "#b5a1e2",
  preset9: "#daaec2",
  preset10: "#dad9dc",
  preset11: "#6b7f8e",
  preset12: "#bfbfbf",
  preset13: "#6f6f6f",
  preset14: "#4f4f4f",
  preset15: "#c11a25",
  preset16: "#e2620d",
  preset17: "#c79930",
  preset18: "#b5b81c",
  preset19: "#307e22",
  preset20: "#1a8667",
  preset21: "#7b8d3c",
  preset22: "#3d5aa8",
  preset23: "#6c3a9b",
  preset24: "#a83d7c",
};

export function categoryColor(preset: string): string {
  return PRESET_COLORS[preset] ?? "#bfbfbf";
}

export function MailboxCategories() {
  const { selectedMailbox } = useSelectedMailbox();
  const [categories, setCategories] = useState<OutlookCategoryRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    setCategories([]);
    void (async () => {
      try {
        const resp = await api.getEmailCategories(selectedMailbox);
        if (!cancelled) setCategories(resp.categories);
      } catch {
        // Silent: the legend simply doesn't appear.
        if (!cancelled) setCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedMailbox]);

  if (categories.length === 0) return null;

  return (
    <li className="sidebar-folder-categories">
      <header className="sidebar-folder-favorites-heading">CATEGORIES</header>
      <ul className="sidebar-list">
        {categories.map((c) => (
          <li key={c.id} className="sidebar-category-row">
            <span
              className="sidebar-category-dot"
              style={{ background: categoryColor(c.color) }}
              aria-hidden
            />
            <span className="sidebar-category-name">{c.displayName}</span>
          </li>
        ))}
      </ul>
    </li>
  );
}
